import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Link as MuiLink,
  IconButton,
  Stack,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import instagram from "../Images/instagram.png";
import Whatsapp from "../Images/apple.png";
import Gmail from "../Images/gmail.png";
import FoterLogo from "../Images/Footer/15.jpg";
import { NAV } from "../Navbar/navbarTheme";

const FOOTER_LINKS = [
  { label: "About us", to: "/AboutUs" },
  { label: "Contact us", to: "/ContactUs" },
  { label: "Our services", to: "/OurServices" },
  { label: "Events & Community", to: "/EventsAndCommunity" },
  { label: "FAQ", to: "/FAQ" },
];

const ACCOUNT_LINKS_STATIC = [
  { label: "Terms and conditions", to: "/TermsAndConditions" },
  { label: "Privacy policy", to: "/PrivacyPolicy" },
  { label: "Modern Slavery Statement", to: "/ModernSlaveryStatement" },
];

const columnHeadingSx = {
  fontSize: { xs: "0.9375rem", md: "1rem" },
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: NAV.ink,
  mb: { xs: 1.75, md: 2 },
};

const footerListLinkSx = {
  display: "block",
  fontSize: { xs: "1rem", md: "1.0625rem" },
  fontWeight: 400,
  color: NAV.ink,
  textDecoration: "none",
  lineHeight: 1.65,
  py: 0.5,
  transition: NAV.transition,
  "&:hover": {
    color: NAV.navy,
    textDecoration: "underline",
  },
};

const socialHref = {
  instagram:
    "https://www.instagram.com/uk_to_mars?igsh=cjViOWRuN3owcHJp&utm_source=qr",
  linkedin: "https://www.linkedin.com/company/uktomars/",
  whatsapp: "http://wa.me/447564627910",
};

function FooterColumnHeading({ children }) {
  return (
    <Typography component="h2" sx={columnHeadingSx}>
      {children}
    </Typography>
  );
}

function FooterNavLinks({ items, ariaLabel }) {
  return (
    <Stack component="nav" spacing={0.5} aria-label={ariaLabel}>
      {items.map((item) => (
        <MuiLink
          key={item.label}
          component={Link}
          to={item.to}
          sx={footerListLinkSx}
        >
          {item.label}
        </MuiLink>
      ))}
    </Stack>
  );
}

const Footer = () => {
  const year = new Date().getFullYear();
  const authUser = useSelector((state) => state.auth.user);
  const authToken = useSelector((state) => state.auth.token);
  const hasProfile = Boolean(authUser || authToken);

  const accountLinks = useMemo(
    () => [
      { label: "My account", to: hasProfile ? "/Profile" : "/Register" },
      ...ACCOUNT_LINKS_STATIC,
    ],
    [hasProfile],
  );

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        bgcolor: "#f6f7f9",
        color: NAV.ink,
        mt: { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          px: { xs: 2.5, sm: 3, md: 4 },
          py: { xs: 3.5, md: 5 },
        }}
      >
        <Grid container spacing={{ xs: 4, md: 5 }}>
          {/* Brand & address */}
          <Grid item xs={12} sm={6} md={3}>
            <Box
              component="img"
              src={FoterLogo}
              alt="UK TO MARS LTD"
              sx={{
                display: "block",
                height: { xs: 64, sm: 72, md: 80 },
                width: "auto",
                maxWidth: { xs: 240, md: 280 },
                mb: 2,
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "1rem", md: "1.0625rem" },
                fontWeight: 700,
                color: NAV.ink,
                lineHeight: 1.45,
                mb: 1,
              }}
            >
              Independent UK FMCG Wholesaler &amp; Exporter
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "1rem", md: "1.0625rem" },
                fontWeight: 700,
                color: NAV.ink,
                mb: 1.25,
              }}
            >
              UK TO MARS LTD
            </Typography>
            <Typography
              component="address"
              sx={{
                fontStyle: "normal",
                fontSize: { xs: "0.9375rem", md: "1rem" },
                color: NAV.ink,
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              15 Little Balmer
              <br />
              Buckingham Industrial Estate
              <br />
              Buckingham, Buckinghamshire
              <br />
              MK18 1TF, United Kingdom
            </Typography>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <IconButton
                component="a"
                href={socialHref.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                sx={{
                  width: { xs: 44, md: 48 },
                  height: { xs: 44, md: 48 },
                  border: `1px solid ${NAV.line}`,
                  bgcolor: NAV.surface,
                  p: 1,
                  "&:hover": { bgcolor: NAV.navyMuted },
                }}
              >
                <Box
                  component="img"
                  src={instagram}
                  alt=""
                  sx={{ width: 26, height: 26, objectFit: "contain" }}
                />
              </IconButton>
              <IconButton
                component="a"
                href={socialHref.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={{
                  width: { xs: 44, md: 48 },
                  height: { xs: 44, md: 48 },
                  border: `1px solid ${NAV.line}`,
                  bgcolor: NAV.surface,
                  color: "#0A66C2",
                  "&:hover": { bgcolor: NAV.navyMuted },
                }}
              >
                <LinkedInIcon sx={{ fontSize: { xs: 26, md: 28 } }} />
              </IconButton>
              <IconButton
                component="a"
                href={socialHref.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                sx={{
                  width: { xs: 44, md: 48 },
                  height: { xs: 44, md: 48 },
                  border: `1px solid ${NAV.line}`,
                  bgcolor: NAV.surface,
                  p: 1,
                  "&:hover": { bgcolor: NAV.navyMuted },
                }}
              >
                <Box
                  component="img"
                  src={Whatsapp}
                  alt=""
                  sx={{ width: 26, height: 26, objectFit: "contain" }}
                />
              </IconButton>
            </Stack>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterColumnHeading>Contact</FooterColumnHeading>
            <Stack spacing={1.75}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  component="img"
                  src={Gmail}
                  alt=""
                  sx={{ width: 24, height: 24, flexShrink: 0 }}
                />
                <MuiLink
                  href="mailto:sales@uktomars.co.uk"
                  sx={{
                    fontSize: { xs: "1rem", md: "1.0625rem" },
                    color: NAV.ink,
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                    "&:hover": { color: NAV.navy },
                  }}
                >
                  sales@uktomars.co.uk
                </MuiLink>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <PhoneIcon
                  sx={{ fontSize: { xs: 24, md: 26 }, color: NAV.ink }}
                />
                <MuiLink
                  href="tel:+441280733473"
                  sx={{
                    fontSize: { xs: "1rem", md: "1.0625rem" },
                    color: NAV.ink,
                    textDecoration: "none",
                    "&:hover": { color: NAV.navy, textDecoration: "underline" },
                  }}
                >
                  +44 1280 733473
                </MuiLink>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  component="img"
                  src={Whatsapp}
                  alt=""
                  sx={{ width: 24, height: 24, flexShrink: 0 }}
                />
                <MuiLink
                  href={socialHref.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: { xs: "1rem", md: "1.0625rem" },
                    color: NAV.ink,
                    textDecoration: "none",
                    "&:hover": { color: NAV.navy, textDecoration: "underline" },
                  }}
                >
                  +44 7564 627910
                </MuiLink>
              </Box>
            </Stack>
          </Grid>

          {/* Links */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterColumnHeading>Links</FooterColumnHeading>
            <FooterNavLinks items={FOOTER_LINKS} ariaLabel="Footer links" />
          </Grid>

          {/* Account */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterColumnHeading>Account</FooterColumnHeading>
            <FooterNavLinks items={accountLinks} ariaLabel="Account links" />
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid #b8d4e8",
          bgcolor: "#f6f7f9",
        }}
      >
        <Box
          sx={{
            maxWidth: 1480,
            mx: "auto",
            px: { xs: 2.5, sm: 3, md: 4 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
              lineHeight: 1.6,
              color: NAV.ink,
              textAlign: { xs: "left", md: "left" },
            }}
          >
            Copyright © {year} All Rights Reserved. UK TO MARS Ltd is a company
            registered in England, United Kingdom | 15 Little Balmer, Buckingham
            Industrial Estate Buckingham, MK18 1TF | Company registration
            number: 15764998 | VAT registration number: GB 473487946.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
